/**
 * WebSocket Authentication Service
 *
 * Handles authentication for WebSocket connections when using httpOnly cookies.
 * This service fetches a short-lived token from the backend that can be used
 * for WebSocket authentication.
 *
 * PROACTIVE REFRESH: When a token is about to expire, this service notifies
 * registered listeners so they can trigger a socket reconnection with fresh auth.
 */

import { store } from '@/core/store';
import { authApi } from '@/api/features/auth';
import { authLogger } from '@/core/utils/logger';
import { logAsyncError } from '@/shared/utils/asyncHelpers';
import { extractErrorMessage } from '@/shared/utils/error';
import {
  isSessionInvalidated as isSessionInvalidatedFlag,
  markSessionInvalidated as markSessionInvalidatedFlag,
  resetSessionInvalidated as resetSessionInvalidatedFlag,
} from '@/api/core/sessionManager';

interface WebSocketToken {
  token: string;
  expiresIn: number; // seconds
  expiresAt?: number; // timestamp when we should refresh (30s before actual expiry)
  actualExpiresAt?: number; // timestamp of actual token expiry
}

interface WsTokenResponse {
  data?: { token?: string; expiresIn?: number };
  token?: string;
  expiresIn?: number;
}

/** Options for getToken() */
interface GetTokenOptions {
  /** Force a fresh token fetch, ignoring cache */
  forceRefresh?: boolean;
}

/** Typed result from token fetch with failure classification */
export interface WsTokenResult {
  token: string | null;
  failureReason?: 'session_expired' | 'transient' | 'unknown';
}

/** Callback type for token expiry notifications */
type TokenExpiryCallback = (reason: string) => void;

class WebSocketAuthService {
  private static instance: WebSocketAuthService;
  private currentToken: WebSocketToken | null = null;
  private refreshTimer?: NodeJS.Timeout;
  // Track pending token fetch to prevent concurrent requests (race condition fix)
  private pendingTokenPromise: Promise<string | null> | null = null;
  private pendingTokenWithStatusPromise: Promise<WsTokenResult> | null = null;

  // Callback for notifying when token is about to expire (proactive refresh)
  private onTokenExpiringCallback: TokenExpiryCallback | null = null;

  // Track if proactive refresh is in progress to prevent storms
  private proactiveRefreshInProgress = false;
  private proactiveRefreshCooldownTimer?: NodeJS.Timeout;

  // Last failure reason for diagnostics
  private lastFailureReason: WsTokenResult['failureReason'] | null = null;

  private constructor() {}

  public static getInstance(): WebSocketAuthService {
    if (!WebSocketAuthService.instance) {
      WebSocketAuthService.instance = new WebSocketAuthService();
    }
    return WebSocketAuthService.instance;
  }

  /**
   * Register a callback to be notified when the token is about to expire.
   * This allows SocketClient to proactively refresh auth before disconnection.
   */
  public onTokenExpiring(callback: TokenExpiryCallback | null): void {
    this.onTokenExpiringCallback = callback;
    authLogger.info('[WebSocketAuthService] Token expiry callback registered:', !!callback);
  }

  /**
   * Get a valid WebSocket token, fetching a new one if needed
   * Uses pending promise pattern to prevent concurrent token fetches (race condition)
   *
   * @param options.forceRefresh - If true, fetch a new token even if current is valid
   */
  public async getToken(options?: GetTokenOptions): Promise<string | null> {
    // If session is permanently invalidated, return null immediately
    if (isSessionInvalidatedFlag()) {
      authLogger.info('[WebSocketAuthService] Session invalidated, returning null');
      return null;
    }

    const forceRefresh = options?.forceRefresh ?? false;

    // Check if we have a valid token (skip if forceRefresh)
    if (!forceRefresh && this.currentToken && this.isTokenValid()) {
      authLogger.debug('[WebSocketAuthService] Using cached token (valid)');
      return this.currentToken.token;
    }

    if (forceRefresh) {
      authLogger.info('[WebSocketAuthService] Force refresh requested, clearing cached token');
      // Clear current token to force a fetch
      this.currentToken = null;
    }

    // If a token fetch is already in progress, wait for it instead of starting another
    // This prevents race conditions when getToken() is called multiple times concurrently
    if (this.pendingTokenPromise) {
      authLogger.debug('[WebSocketAuthService] Token fetch already in progress, waiting...');
      return this.pendingTokenPromise;
    }

    // Start a new token fetch and track it
    this.pendingTokenPromise = this.fetchNewToken();

    try {
      return await this.pendingTokenPromise;
    } finally {
      // Clear the pending promise once complete (success or failure)
      this.pendingTokenPromise = null;
    }
  }

  /**
   * Get a token with typed failure information.
   * Same logic as getToken() but returns WsTokenResult with failureReason.
   * Used by SocketClient to distinguish session_expired from transient failures.
   */
  public async getTokenWithStatus(options?: GetTokenOptions): Promise<WsTokenResult> {
    // If session is permanently invalidated, return immediately
    if (isSessionInvalidatedFlag()) {
      authLogger.info('[WebSocketAuthService] Session invalidated, returning session_expired');
      return { token: null, failureReason: 'session_expired' };
    }

    const forceRefresh = options?.forceRefresh ?? false;

    // Check if we have a valid token (skip if forceRefresh)
    if (!forceRefresh && this.currentToken && this.isTokenValid()) {
      authLogger.debug('[WebSocketAuthService] Using cached token (valid)');
      return { token: this.currentToken.token };
    }

    if (forceRefresh) {
      this.currentToken = null;
    }

    // If a typed fetch is already in progress, wait for it
    if (this.pendingTokenWithStatusPromise) {
      authLogger.debug('[WebSocketAuthService] Token fetch (typed) already in progress, waiting...');
      return this.pendingTokenWithStatusPromise;
    }

    // Start a new typed token fetch
    this.pendingTokenWithStatusPromise = this.fetchNewTokenWithStatus();

    try {
      return await this.pendingTokenWithStatusPromise;
    } finally {
      this.pendingTokenWithStatusPromise = null;
    }
  }

  /**
   * Internal method to actually fetch a new token
   */
  private async fetchNewToken(): Promise<string | null> {
    try {
      authLogger.info('[WebSocketAuthService] Fetching new WebSocket token...');
      const fetchStartTime = Date.now();

      const result = await store.dispatch(
        authApi.endpoints.getWsToken.initiate(undefined)
      ).unwrap() as WsTokenResponse;

      // Handle the backend response format with runtime validation
      const data = result.data ?? result;
      const token = data.token;
      const expiresIn = data.expiresIn;

      if (!token || typeof expiresIn !== 'number') {
        throw new Error('Invalid WebSocket token response: missing token or expiresIn');
      }

      const now = Date.now();
      const actualExpiresAt = now + (expiresIn * 1000);
      // Refresh 30 seconds before actual expiry (or half the TTL if TTL < 60s for short tokens)
      const refreshBuffer = Math.min(30000, (expiresIn * 1000) / 2);
      const expiresAt = actualExpiresAt - refreshBuffer;

      // Store the token with expiry times
      this.currentToken = {
        token,
        expiresIn,
        expiresAt, // When we should trigger proactive refresh
        actualExpiresAt // When token actually expires
      };

      // Set up automatic refresh
      this.scheduleRefresh();

      const fetchDuration = Date.now() - fetchStartTime;
      authLogger.info('[WebSocketAuthService] Got new WebSocket token', {
        expiresIn,
        refreshIn: Math.round((expiresAt - now) / 1000) + 's',
        fetchDuration: fetchDuration + 'ms'
      });
      return this.currentToken.token;
    } catch (error) {
      authLogger.error('[WebSocketAuthService] Failed to fetch token:', error);
      logAsyncError(error, { operation: 'fetchWebSocketToken' });
      return null;
    }
  }

  /**
   * Internal method to fetch a new token with typed failure classification.
   * Classifies errors as session_expired, transient, or unknown.
   */
  private async fetchNewTokenWithStatus(): Promise<WsTokenResult> {
    const reqId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    try {
      authLogger.info('[WebSocketAuthService] Fetching new WebSocket token (typed)...', { reqId });
      const fetchStartTime = Date.now();

      const result = await store.dispatch(
        authApi.endpoints.getWsToken.initiate(undefined)
      ).unwrap() as WsTokenResponse;

      // Handle the backend response format with runtime validation
      const data = result.data ?? result;
      const token = data.token;
      const expiresIn = data.expiresIn;

      if (!token || typeof expiresIn !== 'number') {
        this.lastFailureReason = 'unknown';
        return { token: null, failureReason: 'unknown' };
      }

      const now = Date.now();
      const actualExpiresAt = now + (expiresIn * 1000);
      const refreshBuffer = Math.min(30000, (expiresIn * 1000) / 2);
      const expiresAt = actualExpiresAt - refreshBuffer;

      this.currentToken = {
        token,
        expiresIn,
        expiresAt,
        actualExpiresAt
      };

      this.scheduleRefresh();

      const fetchDuration = Date.now() - fetchStartTime;
      this.lastFailureReason = null;

      if (process.env.NODE_ENV === 'development') {
        authLogger.info('[WebSocketAuthService] Token fetch (typed) succeeded', {
          reqId,
          expiresIn,
          refreshIn: Math.round((expiresAt - now) / 1000) + 's',
          fetchDuration: fetchDuration + 'ms'
        });
      }

      return { token: this.currentToken.token };
    } catch (error: unknown) {
      const err = error as { status?: number | string; error?: string; message?: string; name?: string };
      let failureReason: WsTokenResult['failureReason'] = 'unknown';

      // Classify the error
      if (typeof err.status === 'number' && err.status === 401) {
        // 401 from ws-token means baseQueryWithRetry already tried refresh and it failed
        failureReason = 'session_expired';
      } else if (
        err.status === 'FETCH_ERROR' ||
        err.name === 'AbortError' ||
        String(err.error || err.message || '').toLowerCase().includes('timeout')
      ) {
        failureReason = 'transient';
      }

      this.lastFailureReason = failureReason;

      if (process.env.NODE_ENV === 'development') {
        authLogger.error('[WebSocketAuthService] Token fetch (typed) failed', {
          reqId,
          status: err.status,
          failureReason,
          error: extractErrorMessage(error)
        });
      } else {
        authLogger.error('[WebSocketAuthService] Failed to fetch token:', error);
      }

      logAsyncError(error, { operation: 'fetchWebSocketTokenWithStatus' });
      return { token: null, failureReason };
    }
  }

  /**
   * Mark the session as permanently invalidated.
   * After this, getToken() and getTokenWithStatus() return immediately without fetching.
   * This is permanent until page reload.
   */
  public markSessionInvalidated(): void {
    if (isSessionInvalidatedFlag()) return;
    authLogger.info('[WebSocketAuthService] Session marked as permanently invalidated');
    markSessionInvalidatedFlag();
    this.currentToken = null;
    // Stop refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    // Stop cooldown timer
    if (this.proactiveRefreshCooldownTimer) {
      clearTimeout(this.proactiveRefreshCooldownTimer);
      this.proactiveRefreshCooldownTimer = undefined;
    }
  }

  /**
   * Check if session has been permanently invalidated
   */
  public isSessionInvalidated(): boolean {
    return isSessionInvalidatedFlag();
  }

  /**
   * Reset session invalidation — allows token fetches to resume.
   * Used by recovery code (e.g. tab visibility handler) after confirming
   * the session is actually still valid.
   */
  public resetSessionInvalidated(): void {
    if (!isSessionInvalidatedFlag()) return;
    authLogger.info('[WebSocketAuthService] Session invalidation reset — token fetches re-enabled');
    resetSessionInvalidatedFlag();
    this.lastFailureReason = null;
  }

  /**
   * Check if the current token is still valid
   */
  private isTokenValid(): boolean {
    if (!this.currentToken || !this.currentToken.expiresAt) {
      return false;
    }
    return Date.now() < this.currentToken.expiresAt;
  }

  /**
   * Schedule automatic token refresh before expiry.
   * PROACTIVE REFRESH: Instead of just clearing the token, we notify
   * the registered callback (SocketClient) to trigger a controlled reconnect.
   */
  private scheduleRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    if (!this.currentToken || !this.currentToken.expiresAt) {
      return;
    }

    // Calculate when to trigger proactive refresh
    const refreshIn = this.currentToken.expiresAt - Date.now();

    authLogger.info('[WebSocketAuthService] Scheduling proactive refresh', {
      refreshIn: Math.round(refreshIn / 1000) + 's',
      expiresAt: new Date(this.currentToken.expiresAt).toISOString()
    });

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        this.triggerProactiveRefresh();
      }, refreshIn);
    } else {
      // Token already expired or about to - trigger immediately
      authLogger.warn('[WebSocketAuthService] Token already at/past refresh threshold, triggering immediately');
      this.triggerProactiveRefresh();
    }
  }

  /**
   * Trigger proactive token refresh and notify SocketClient to reconnect.
   * Uses a guard to prevent refresh storms.
   */
  private triggerProactiveRefresh(): void {
    // Don't refresh if session is permanently invalidated
    if (isSessionInvalidatedFlag()) {
      authLogger.info('[WebSocketAuthService] Session invalidated, skipping proactive refresh');
      return;
    }

    // Guard against refresh storms
    if (this.proactiveRefreshInProgress) {
      authLogger.debug('[WebSocketAuthService] Proactive refresh already in progress, skipping');
      return;
    }

    authLogger.info('[WebSocketAuthService] Token expiring soon, triggering proactive refresh');
    this.proactiveRefreshInProgress = true;

    // Clear the current token so next getToken() fetches fresh
    this.currentToken = null;

    // Notify SocketClient to trigger a controlled reconnect with fresh token
    if (this.onTokenExpiringCallback) {
      try {
        authLogger.info('[WebSocketAuthService] Notifying SocketClient of token expiry');
        this.onTokenExpiringCallback('token_expiring');
      } catch (error) {
        authLogger.error('[WebSocketAuthService] Error in token expiry callback:', error);
      }
    } else {
      authLogger.warn('[WebSocketAuthService] No callback registered for token expiry - socket may disconnect on next auth check');
    }

    // Reset the guard after a short delay to allow the next refresh cycle
    this.proactiveRefreshCooldownTimer = setTimeout(() => {
      this.proactiveRefreshCooldownTimer = undefined;
      this.proactiveRefreshInProgress = false;
    }, 5000); // 5 second cooldown
  }

  /**
   * Clear the current token and cancel refresh timer
   */
  public clearToken(): void {
    authLogger.info('[WebSocketAuthService] Clearing token and timers');
    this.currentToken = null;
    this.proactiveRefreshInProgress = false;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    if (this.proactiveRefreshCooldownTimer) {
      clearTimeout(this.proactiveRefreshCooldownTimer);
      this.proactiveRefreshCooldownTimer = undefined;
    }
  }

  /**
   * Get diagnostic info about current token state (for debugging)
   */
  public getTokenState(): {
    hasToken: boolean;
    isValid: boolean;
    expiresIn: number | null;
    refreshScheduled: boolean;
    sessionInvalidated: boolean;
    lastFailureReason: WsTokenResult['failureReason'] | null;
  } {
    const now = Date.now();
    return {
      hasToken: !!this.currentToken,
      isValid: this.isTokenValid(),
      expiresIn: this.currentToken?.actualExpiresAt
        ? Math.round((this.currentToken.actualExpiresAt - now) / 1000)
        : null,
      refreshScheduled: !!this.refreshTimer,
      sessionInvalidated: isSessionInvalidatedFlag(),
      lastFailureReason: this.lastFailureReason
    };
  }
}

export const wsAuthService = WebSocketAuthService.getInstance();